import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Target, Lightbulb, Zap, TrendingUp } from 'lucide-react';

const stats = [
  { value: '150+', label: 'Proyectos Entregados' },
  { value: '98%', label: 'Satisfacción del Cliente' },
  { value: '+5', label: 'Años de Experiencia' },
  { value: '+16', label: 'Clientes Regionales' },
];

const values = [
  { icon: Target, title: 'Estrategia Primero', description: 'Cada decisión respaldada por datos e investigación' },
  { icon: Lightbulb, title: 'Excelencia Creativa', description: 'Diseños que cautivan y convierten' },
  { icon: Zap, title: 'Impulsados por Tech', description: 'Aprovechando tecnología de vanguardia' },
  { icon: TrendingUp, title: 'Enfocados en Crecimiento', description: 'Resultados que impulsan el negocio' },
];

export default function About() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      id="about"
      ref={ref}
      className="relative py-24 md:py-32 bg-black"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="grid md:grid-cols-2 gap-12 md:gap-20 mb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-mono tracking-[0.3em] uppercase text-[#D4AF37] mb-4 block">
              About Us
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.1]">
              Sobre Orian
              <br />
              <span className="gold-text">Partners Studio</span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col justify-end"
          >
            <p className="text-base md:text-lg text-zinc-400 leading-relaxed">
              Orian Partners Studio es un estudio creativo de marketing enfocado en construir
              posicionamiento estratégico, infraestructura digital y sistemas de crecimiento
              escalables para negocios modernos.
            </p>
            <p className="text-base md:text-lg text-zinc-400 leading-relaxed mt-4">
              Combinamos creatividad, estrategia, tecnología y contenido para ayudar
              a las empresas a fortalecer su presencia de marca y acelerar su crecimiento.
            </p>
          </motion.div>
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-20 py-12 border-y border-white/10"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
              className="text-left"
              data-testid={`stat-${index}`}
            >
              <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#D4AF37] mb-2">
                {stat.value}
              </div>
              <div className="text-xs md:text-sm tracking-[0.1em] uppercase text-zinc-500">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Values Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((value, index) => (
            <motion.div
              key={value.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
              className="group p-8 bg-zinc-950/50 border border-white/5 hover:border-[#D4AF37]/50 transition-all duration-500"
              data-testid={`value-${index}`}
            >
              <value.icon
                size={32}
                className="text-zinc-500 group-hover:text-[#D4AF37] transition-colors duration-300 mb-6"
              />
              <h3 className="text-lg font-semibold text-white group-hover:text-[#D4AF37] transition-colors duration-300 mb-3">
                {value.title}
              </h3>
              <p className="text-sm text-zinc-500 leading-relaxed">
                {value.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
